import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { designSystem } from '@yola/ws-ui';
import { view } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import attributes from '../constants/attributes';

const { Box, ActionRadioGroup } = designSystem;
const { Context: ElementSettingsContext } = elementSettings;

const HORIZONTAL_ALIGNMENT_ID = 'horizontal-alignment';
const VERTICAL_ALIGNMENT_ID = 'vertical-alignment';

const getSelectedValue = (state, elementId, choices, attribute) => {
  let value =
    get(state, `${elementId}.${attribute}`) ||
    view.accessors.getElementAttribute(elementId, attribute);

  if (!value) {
    const element = view.accessors.getLiveElement(elementId);
    const computedStyles = window.getComputedStyle(element);

    if (attribute === attributes.HORIZONTAL_ALIGN) {
      value = computedStyles.justifyContent;
    } else {
      value = computedStyles.alignItems;
    }
  }

  return choices.find((choice) => choice.value === value)?.id || choices[0].value;
};

const horizontalChoices = [
  {
    id: 'left',
    glyph: 'align-start-horizontal',
    value: 'left',
  },
  {
    id: 'center',
    glyph: 'align-center-horizontal',
    value: 'center',
  },
  {
    id: 'right',
    glyph: 'align-end-horizontal',
    value: 'right',
  },
];

const verticalChoices = [
  {
    id: 'start',
    glyph: 'align-start-vertical',
    value: 'start',
  },
  {
    id: 'center',
    glyph: 'align-center-vertical',
    value: 'center',
  },
  {
    id: 'end',
    glyph: 'align-end-vertical',
    value: 'end',
  },
];

function ColumnsAlignmentsControl({ elementId, onChange }) {
  const { state } = useContext(ElementSettingsContext);

  const handleChange = useCallback(
    (targetId, targetAttributes, value) => {
      onChange({
        controlId: targetId,
        elementIds: [elementId],
        attributes: targetAttributes,
        value,
      });
    },
    [elementId, onChange]
  );

  const handleHorizontalChange = useCallback(
    ({ value }) => {
      handleChange(HORIZONTAL_ALIGNMENT_ID, [attributes.HORIZONTAL_ALIGN], value);
    },
    [handleChange]
  );

  const handleVerticalChange = useCallback(
    ({ value }) => {
      handleChange(VERTICAL_ALIGNMENT_ID, [attributes.VERTICAL_ALIGN], value);
    },
    [handleChange]
  );

  if (!elementId) return null;

  const horizontalSelected = getSelectedValue(
    state,
    elementId,
    horizontalChoices,
    attributes.HORIZONTAL_ALIGN
  );
  const verticalSelected = getSelectedValue(
    state,
    elementId,
    verticalChoices,
    attributes.VERTICAL_ALIGN
  );

  return (
    <Box padding="spacing-s">
      <div className="ws-columns-alignments-control">
        <ActionRadioGroup
          options={horizontalChoices}
          selectedId={horizontalSelected}
          gap="auto"
          onChange={handleHorizontalChange}
        />
        <ActionRadioGroup
          options={verticalChoices}
          selectedId={verticalSelected}
          gap="auto"
          onChange={handleVerticalChange}
        />
      </div>
    </Box>
  );
}

ColumnsAlignmentsControl.propTypes = {
  elementId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ColumnsAlignmentsControl;
